import React, { useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import SweetAlert from "react-bootstrap-sweetalert";
import redirectURL from "../redirectURL";

const RawCourierTrackingGrid = () => {
    const [rowData, setRowData] = useState([]);
    const [allLrNos, setAllLrNos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);

    const [filters, setFilters] = useState({
        lrNo: "",
        startDate: "",
        endDate: "",
    });

    const columnDefs = [
        { headerName: "LR No", field: "lr_no", filter: true },
        { headerName: "Status", field: "last_status", filter: true },
        { headerName: "Location", field: "last_location", filter: true },
        {
            headerName: "Date",
            field: "last_date",
            filter: true,
            valueGetter: (params) =>
                params.data.last_date ? getHyphenDDMMYYYY(params.data.last_date) : "",
        },
        { headerName: "Transporter Name", field: "transporter_name", filter: true },
        {
            headerName: "Processed Date",
            field: "create_date",
            hide: true,
            valueGetter: (params) =>
                params.data.create_date
                    ? getHyphenDDMMYYYY(params.data.create_date)
                    : "",
        },
        {
            headerName: "Received Date",
            field: "received_date",
            filter: true,
            valueGetter: (params) =>
                params.data.received_date
                    ? getHyphenDDMMYYYY(params.data.received_date)
                    : "",
        },
    ];

    const defaultColDef = {
        sortable: true,
        resizable: true,
        filter: true,
    };

    useEffect(() => {
        const today = new Date();
        const twoDaysAgo = new Date();
        twoDaysAgo.setDate(today.getDate() - 2);

        const start = twoDaysAgo.toISOString().slice(0, 10);
        const end = today.toISOString().slice(0, 10);

        const defaultFilters = { lrNo: "", startDate: start, endDate: end };
        setFilters(defaultFilters);
        fetchCourierData(defaultFilters);
    }, []);

    const fetchCourierData = (overrideFilters = filters) => {
        const params = {
            lr_no: overrideFilters.lrNo,
            start_date: overrideFilters.startDate,
            end_date: overrideFilters.endDate,
        };

        setLoading(true);
        redirectURL
            .post("/consignments/getRawCourierTracking", params)
            .then((response) => {
                const data = response.data?.data || [];
                setRowData(data);

                if (allLrNos.length === 0) {
                    const uniqueIRs = [...new Set(data.map((item) => item.lr_no))];
                    setAllLrNos(uniqueIRs);
                }
            })
            .catch((error) => console.error("API error:", error))
            .finally(() => setLoading(false));
    };

    const validateAndApply = () => {
        if (!filters.startDate || !filters.endDate) {
            setAlert(
                <SweetAlert danger title="Validation Error" onConfirm={() => setAlert(null)}>
                    Please select both Start Date and End Date.
                </SweetAlert>
            );
            return;
        }

        if (new Date(filters.startDate) > new Date(filters.endDate)) {
            setAlert(
                <SweetAlert danger title="Invalid Dates" onConfirm={() => setAlert(null)}>
                    Start Date cannot be greater than End Date.
                </SweetAlert>
            );
            return;
        }

        fetchCourierData(filters);
    };

    const handleReset = () => {
        const today = new Date();
        const twoDaysAgo = new Date();
        twoDaysAgo.setDate(today.getDate() - 2);

        const resetFilters = {
            lrNo: "",
            startDate: twoDaysAgo.toISOString().slice(0, 10),
            endDate: today.toISOString().slice(0, 10),
        };
        setFilters(resetFilters);
        fetchCourierData(resetFilters);
    };

    const onGridReady = (params) => {
        params.api.sizeColumnsToFit();
    };

    return (
        <div className="container-fluid mt-3">
            {loading && (
                <>
                    <div className="overlay-part" style={{ zIndex: 10 }}></div>
                    <div className="dataLoadpage"></div>
                    <div className="dataLoadpageimg" style={{ top: "21em" }}>
                        <div className="loader-box">
                            <div className="loader-box">
                                <div className="rotate dashed colored"></div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <div className="card shadow-sm mb-3">
                <div className="card-body">
                    <h5 className="card-title mb-3">Raw Courier Tracking</h5>
                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label">LR No</label>
                            <input
                                className="form-control small-dropdown"
                                list="lrNos"
                                value={filters.lrNo}
                                onChange={(e) => setFilters({ ...filters, lrNo: e.target.value })}
                                placeholder="Enter LR No"
                            />
                            <datalist id="lrNos">
                                {allLrNos.map((ir, idx) => (
                                    <option key={idx} value={ir} />
                                ))}
                            </datalist>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Start Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={filters.startDate}
                                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                            />
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={filters.endDate}
                                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                            />
                        </div>

                        <div className="col-md-3 d-flex gap-2">
                            <button className="btn btn-primary flex-fill mr-2" onClick={validateAndApply}>
                                Apply
                            </button>
                            <button
                                className="btn btn-success flex-fill reset-btn-rawcourier"
                                onClick={handleReset}
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm">
                <div className="card-body">
                    <h5 className="card-title mb-3">Courier Tracking Data</h5>
                    <div className="ag-theme-balham" style={{ height: "60vh", width: "100%" }}>
                        <AgGridReact
                            columnDefs={columnDefs}
                            rowData={rowData}
                            defaultColDef={defaultColDef}
                            onGridReady={onGridReady}
                            pagination={true}
                            paginationPageSize={50}
                            enableRangeSelection={true}
                            stopEditingWhenGridLosesFocus={true}
                            sideBar={{
                                toolPanels: [
                                    {
                                        id: "columns",
                                        labelDefault: "Columns",
                                        iconKey: "columns",
                                        toolPanel: "agColumnsToolPanel",
                                        toolPanelParams: {
                                            suppressPivotMode: true,
                                            suppressRowGroups: true,
                                            suppressValues: true,
                                        },
                                    },
                                    {
                                        id: "filters",
                                        labelDefault: "Filters",
                                        iconKey: "filter",
                                        toolPanel: "agFiltersToolPanel",
                                    },
                                ],
                            }}
                            statusBar={{
                                statusPanels: [
                                    { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
                                    { statusPanel: "agTotalRowCountComponent", align: "center" },
                                    { statusPanel: "agFilteredRowCountComponent" },
                                    { statusPanel: "agSelectedRowCountComponent" },
                                    { statusPanel: "agAggregationComponent" },
                                ],
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* SweetAlert */}
            {alert}
        </div>
    );
};

export default RawCourierTrackingGrid;

const getHyphenDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

